import punycode from "punycode"
import tlds from "./tlds.json"

const commonSubdomains = ["www", "mail", "ftp", "webmail"]

const allowedProtocols = [
  "https:",
  // "http://", // We don't allow http because it's not secure
  "ftp:",
  "sftp:",
  "irc:",
  "mailto:",
  "tel:",
  "sms:",
  "slack:",
  "whatsapp:",
  "skype:",
  "zoommtg:"
]

type invalidReason =
  | "invalidURL"
  | "missingProtocol"
  | "invalidProtocol"
  | "insecureProtocol"
  | "invalidEmail"
  | "invalidPhoneNumber"
  | "missingPathname"
  | "missingHostname"
  | "hostnameTooLong"
  | "invalidTLD"
  | "invalidSLD"

const invalidReasons: Record<invalidReason, invalidReason> = {
  invalidURL: "invalidURL",
  missingProtocol: "missingProtocol",
  invalidProtocol: "invalidProtocol",
  insecureProtocol: "insecureProtocol",
  invalidEmail: "invalidEmail",
  invalidPhoneNumber: "invalidPhoneNumber",
  missingPathname: "missingPathname",
  missingHostname: "missingHostname",
  hostnameTooLong: "hostnameTooLong",
  invalidTLD: "invalidTLD",
  invalidSLD: "invalidSLD"
}

const lacksProtocol = (url: string) => !/^([a-zA-Z][a-zA-Z\d+\-.]*:)/.test(url)

const validateURL = (urlInput: URL | string): invalidReason | true => {
  let url: URL

  // 1. Check if the URL is relative (starts with '/')
  if (typeof urlInput === "string" && urlInput.startsWith("/")) {
    return urlInput.length > 1 ? true : invalidReasons.missingPathname
  }

  // 2. Use the URL constructor to parse the URL and provide a baseline validation
  try {
    url = new URL(urlInput)
  } catch (e) {
    if (lacksProtocol(urlInput?.toString())) {
      return invalidReasons.missingProtocol
    }
    return invalidReasons.invalidURL
  }

  const { hostname, pathname, protocol } = url

  // 3. Ensure the protocol is one of the allowed protocols
  if (!allowedProtocols.includes(protocol)) {
    if (protocol === "http:") {
      return invalidReasons.insecureProtocol
    }
    return invalidReasons.invalidProtocol
  }

  // 4. Absolute or authoritative URLs have a :// in their protocol and have a hostname
  // Other URLs are relative or schema-specific
  const isAbsolute = url.toString().includes("://")

  // 5. Validate schema-specific URLs (mailto:, tel:, etc.)
  if (!isAbsolute) {
    if (protocol === "mailto:") {
      if (!pathname?.includes("@")) return invalidReasons.invalidEmail

      const emailDomain = pathname.split("@")[1]
      if (validateURL(`https://${emailDomain}`) !== true) {
        return invalidReasons.invalidEmail
      }
    }

    if (
      (protocol === "tel:" || protocol === "sms:") &&
      !/^\+\d{1,15}$/.test(pathname)
    ) {
      return invalidReasons.invalidPhoneNumber
    }

    return !!pathname ? true : invalidReasons.missingPathname
  }

  // 6. Validate the hostname

  // 6.a. Must exist
  if (!hostname) return invalidReasons.missingHostname

  const hostnameParts = hostname.split(".")

  // 6.b. Validate length of hostname
  if (hostname.length > 255 || hostnameParts.some((part) => part.length > 63)) {
    return invalidReasons.hostnameTooLong
  }

  // 6.c. Must have a valid Top Level Domain
  const tld = hostnameParts.pop()
  if (
    !tld ||
    tld.length < 2 ||
    (!tlds.includes(tld.toLowerCase()) &&
      !tlds.includes(punycode.toASCII(tld.toLowerCase())))
  ) {
    return invalidReasons.invalidTLD
  }

  // 6.d. Must have a valid Second Level Domain and it must not be in our list of common subdomains
  const sld = hostnameParts.pop()
  if (!sld || commonSubdomains.includes(sld)) {
    return invalidReasons.invalidSLD
  }

  // 6.e. Must have at least an implicit pathname
  if (pathname === null || pathname === "null") {
    return invalidReasons.missingPathname
  }

  return true
}

export const getValidationMessage = (reason: invalidReason) => {
  let message = "The URL is invalid."

  switch (reason) {
    case invalidReasons.invalidURL:
      message = "The URL is invalid."
      break
    case invalidReasons.invalidProtocol:
      message = "The URL's protocol isn't valid."
      break
    case invalidReasons.missingProtocol:
      message = "The URL is missing a protocol (ex: https://)."
      break
    case invalidReasons.insecureProtocol:
      message = "The URL's protocol (http://) isn't secure."
      break
    case invalidReasons.invalidEmail:
      message = "The email address is invalid."
      break
    case invalidReasons.invalidPhoneNumber:
      message = "The phone number is invalid."
      break
    case invalidReasons.missingPathname:
    case invalidReasons.missingHostname:
      message = "Part of the URL is missing."
      break
    case invalidReasons.hostnameTooLong:
      message = "The URL is too long."
      break
    case invalidReasons.invalidTLD:
    case invalidReasons.invalidSLD:
      message = "The URL is invalid."
      break
    default:
      message = "The URL is invalid."
  }

  // console.warn(`URL validation error. ${reason}: ${message}`)
  return message
}

export default validateURL
